import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Home, FolderOpen, Settings, BarChart3, UploadCloud, Link } from 'lucide-react';
import LevelUpHeader from '../components/LevelUpHeader';
import SunsetBanner from '../components/SunsetBanner';
import LevelUpFooter from '../components/LevelUpFooter';
import COAModal from '../components/COAModal';

const CONVERSATION_GROUP_FIELD = {
  'Conversation (Enterprise & SMC)': 'conversation_enterprise_smc',
  'Conversation (SMB)': 'conversation_smb'
};

function Upload({ user, onLogout }) {
  const navigate = useNavigate();
  const [solutionAreas, setSolutionAreas] = useState({});
  const [conversationGroups, setConversationGroups] = useState({ 'Conversation (Enterprise & SMC)': [], 'Conversation (SMB)': [] });
  const [openConversationGroup, setOpenConversationGroup] = useState(null);
  const [myAssets, setMyAssets] = useState([]);
  const [coaStatus, setCoaStatus] = useState(null);
  const [showCOAModal, setShowCOAModal] = useState(false);
  const [coaLoading, setCoaLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    solution_area: '',
    content_type: 'Presentation',
    target_audiences: [],
    conversation_enterprise_smc: [],
    conversation_smb: [],
    description: '',
    expiry_date: '',
    language: 'English',
    content_build_date: '',
    is_replacement: false,
    replacing_asset_id: ''
  });
  const [file, setFile] = useState(null);
  const [captionFile, setCaptionFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'weblink'
  const [webUrl, setWebUrl] = useState('');

  useEffect(() => {
    fetchTaxonomy();
    fetchMyAssets();
    checkCOAStatus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid^="conversations-field-"]')) {
        setOpenConversationGroup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkCOAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCoaStatus(data);
      
      // Show modal if COA is required but not accepted
      if (data.requires_coa && !data.coa_accepted) {
        setShowCOAModal(true);
      }
    } catch (error) {
      console.error('Failed to check COA status:', error);
    }
  };

  const handleAcceptCOA = async () => {
    setCoaLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coa/accept`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Content Owner Agreement accepted successfully');
        setShowCOAModal(false);
        setCoaStatus({ ...coaStatus, coa_accepted: true });
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to accept agreement');
      }
    } catch (error) {
      console.error('Failed to accept COA:', error);
      toast.error('Failed to accept agreement. Please try again.');
    } finally {
      setCoaLoading(false);
    }
  };

  const handleCancelCOA = () => {
    // Redirect to browse page if user cancels COA
    toast.info('You must accept the Content Owner Agreement to upload content');
    navigate('/browse');
  };

  const fetchTaxonomy = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/solutions`);
      const data = await response.json();
      setSolutionAreas(data.taxonomy);
    } catch (error) {
      console.error('Failed to fetch taxonomy');
    }
    try {
      const convResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/conversations`);
      const convData = await convResponse.json();
      setConversationGroups(convData.conversations || { 'Conversation (Enterprise & SMC)': [], 'Conversation (SMB)': [] });
    } catch (error) {
      console.error('Failed to fetch conversation taxonomy');
    }
  };

  const fetchMyAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets?mine=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setMyAssets(data.assets || []);
    } catch (error) {
      console.error('Failed to fetch assets');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'is_replacement' && !checked && { replacing_asset_id: '' })
    }));
    // Sync upload mode with content type selection
    if (name === 'content_type') {
      if (value === 'Web URL') {
        setUploadMode('weblink');
      } else {
        setUploadMode('file');
      }
    }
  };

  const handleAudienceToggle = (audience) => {
    setFormData(prev => ({
      ...prev,
      target_audiences: prev.target_audiences.includes(audience)
        ? prev.target_audiences.filter(a => a !== audience)
        : [...prev.target_audiences, audience]
    }));
  };

  const handleConversationToggle = (group, value) => {
    const field = CONVERSATION_GROUP_FIELD[group];
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  // File extension mapping for content types
  const contentTypeExtensions = {
    'Presentation': ['.pptx', '.ppt', '.ppsx', '.pps'],
    'PDF': ['.pdf'],
    'Word': ['.docx', '.doc'],
    'Excel Sheet': ['.xlsx', '.xls', '.xlsm'],
    'Video': ['.mp4', '.avi', '.mov', '.wmv', '.mkv', '.webm'],
    'Caption': ['.vtt', '.srt']
  };

  // Maximum file size: 2GB
  const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB in bytes
  const MAX_FILE_SIZE_DISPLAY = "2 GB";

  // Blocked file extensions (security)
  const BLOCKED_EXTENSIONS = [
    '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
    '.js', '.vbs', '.vbe', '.jse', '.ws', '.wsf', '.wsc', '.wsh',
    '.ps1', '.psm1', '.psd1', '.sh', '.bash', '.zsh', '.csh',
    '.dll', '.sys', '.drv', '.ocx', '.cpl', '.inf', '.reg',
    '.hta', '.jar', '.jnlp', '.app', '.action', '.bin',
    '.docm', '.xlsm', '.pptm', '.dotm', '.xltm', '.potm',
    '.iso', '.img', '.dmg', '.torrent', '.lnk', '.url',
    '.php', '.asp', '.aspx', '.jsp', '.cgi', '.pl', '.py'
  ];

  const validateFileType = (selectedFile, contentType) => {
    if (!selectedFile || !contentType) return { valid: true };
    
    const fileName = selectedFile.name.toLowerCase();
    
    // Check for blocked extensions first (security)
    for (const blockedExt of BLOCKED_EXTENSIONS) {
      if (fileName.endsWith(blockedExt)) {
        return {
          valid: false,
          error: `File type "${blockedExt}" is not allowed for security reasons.`
        };
      }
    }
    
    // Check for suspicious double extensions
    const parts = fileName.split('.');
    if (parts.length > 2) {
      const secondLastExt = '.' + parts[parts.length - 2];
      if (BLOCKED_EXTENSIONS.includes(secondLastExt)) {
        return {
          valid: false,
          error: `File has a suspicious double extension and cannot be uploaded.`
        };
      }
    }
    
    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size (${(selectedFile.size / (1024*1024*1024)).toFixed(2)} GB) exceeds maximum allowed size of ${MAX_FILE_SIZE_DISPLAY}.`
      };
    }
    
    // Check content type specific extensions
    const allowedExtensions = contentTypeExtensions[contentType] || [];
    
    if (allowedExtensions.length === 0) {
      // If no specific extensions defined, allow any
      return { valid: true };
    }
    
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      return {
        valid: false,
        error: `File type mismatch: Selected "${contentType}" but uploaded file is "${fileName}". Allowed extensions: ${allowedExtensions.join(', ')}`
      };
    }
    
    return { valid: true };
  };

  const uploadToAzure = async (fileToUpload, purpose) => {
    const token = localStorage.getItem('token');
    setProgress(5);
    
    // Go directly to proxy upload (more reliable in enterprise environments)
    // Direct Azure upload often blocked by corporate firewalls/proxies
    console.log('Starting proxy upload for:', fileToUpload.name, 'Size:', fileToUpload.size);
    
    try {
      const blobName = await uploadViaProxy(fileToUpload, purpose, token);
      return blobName;
    } catch (proxyError) {
      console.error('Proxy upload failed:', proxyError);
      
      // Try direct upload as fallback (in case proxy has issues)
      console.log('Trying direct upload as fallback...');
      try {
        // Get SAS token
        const sasResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/sas-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file_name: fileToUpload.name,
            content_type: fileToUpload.type || 'application/octet-stream',
            file_size: fileToUpload.size,
            purpose: purpose || 'content'
          })
        });

        if (!sasResponse.ok) {
          throw new Error('Failed to get SAS token');
        }
        
        const sasData = await sasResponse.json();
        const blobName = await uploadDirectToAzure(fileToUpload, sasData.sas_url, sasData.blob_name);
        return blobName;
      } catch (directError) {
        console.error('Direct upload also failed:', directError);
        // Re-throw the original proxy error as it's more informative
        throw proxyError;
      }
    }
  };

  // Direct upload to Azure using SAS URL
  const uploadDirectToAzure = (fileToUpload, sasUrl, blobName) => {
    return new Promise(async (resolve, reject) => {
      console.log('Direct upload - SAS URL:', sasUrl.substring(0, 100) + '...');
      console.log('Direct upload - File size:', fileToUpload.size, 'bytes');
      
      // Try using fetch API first (more reliable CORS handling)
      try {
        const response = await fetch(sasUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': fileToUpload.type || 'application/octet-stream',
          },
          body: fileToUpload,
        });
        
        if (response.ok) {
          console.log('Direct upload succeeded with fetch API');
          setProgress(90);
          resolve(blobName);
        } else {
          const errorText = await response.text();
          console.error('Direct upload failed:', response.status, errorText);
          reject(new Error(`Direct upload failed: ${response.status} - ${errorText}`));
        }
      } catch (fetchError) {
        console.error('Fetch upload error:', fetchError);
        
        // Fallback to XMLHttpRequest for progress tracking
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const uploadProgress = Math.round(15 + (event.loaded / event.total) * 75);
            setProgress(uploadProgress);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('Direct upload succeeded with XHR');
            setProgress(90);
            resolve(blobName);
          } else {
            console.error('XHR upload failed:', xhr.status, xhr.responseText);
            reject(new Error(`Direct upload failed with status ${xhr.status}`));
          }
        });
        
        xhr.addEventListener('error', (e) => {
          console.error('XHR error event:', e);
          reject(new Error('Direct upload failed - CORS or network error'));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new Error('Upload was cancelled'));
        });

        xhr.addEventListener('timeout', () => {
          reject(new Error('Upload timed out'));
        });
        
        xhr.timeout = 30 * 60 * 1000; // 30 minutes for large files
        
        xhr.open('PUT', sasUrl);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', fileToUpload.type || 'application/octet-stream');
        xhr.send(fileToUpload);
      }
    });
  };

  // Fallback: Upload via backend proxy with progress tracking
  const uploadViaProxy = async (fileToUpload, purpose, token) => {
    setProgress(10);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', fileToUpload);
    formDataUpload.append('purpose', purpose || 'content');
    
    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          // Map upload progress from 10% to 85%
          const uploadProgress = Math.round(10 + (event.loaded / event.total) * 75);
          setProgress(uploadProgress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            setProgress(90);
            console.log('Proxy upload successful:', result.blob_name);
            resolve(result.blob_name);
          } catch (e) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          let errorMessage = `Upload failed with status ${xhr.status}`;
          try {
            const errorData = JSON.parse(xhr.responseText);
            errorMessage = errorData.detail || errorMessage;
          } catch (e) {}
          reject(new Error(errorMessage));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });
      
      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timed out. Please try with a smaller file or check your connection.'));
      });
      
      // Set timeout to 10 minutes
      xhr.timeout = 10 * 60 * 1000;
      
      xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/uploads/proxy`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formDataUpload);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Weblink mode ---
    if (uploadMode === 'weblink') {
      if (!webUrl.trim()) { toast.error('Please enter a web URL'); return; }
      try { new URL(webUrl); } catch { toast.error('Please enter a valid URL (must start with http:// or https://)'); return; }
      if (formData.target_audiences.length === 0) { toast.error('Please select at least one target audience'); return; }
      if (formData.conversation_enterprise_smc.length === 0 && formData.conversation_smb.length === 0) { toast.error('Please select at least one value under Conversation (Enterprise & SMC) or Conversation (SMB)'); return; }
      if (!formData.content_build_date) { toast.error('Please set the Version (content build date)'); return; }
      setUploading(true);
      try {
        const token = localStorage.getItem('token');
        const rawTitle = formData.title.trim();
        const finalTitle = rawTitle.toLowerCase().endsWith('(external)') ? rawTitle : `${rawTitle} (External)`;
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/weblink`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: finalTitle,
            solution_area: formData.solution_area,
            target_audiences: formData.target_audiences,
            conversation_enterprise_smc: formData.conversation_enterprise_smc,
            conversation_smb: formData.conversation_smb,
            description: formData.description,
            language: formData.language,
            content_build_date: formData.content_build_date || null,
            expiry_date: formData.expiry_date || null,
            is_replacement: formData.is_replacement,
            replacing_asset_id: formData.replacing_asset_id || null,
            web_url: webUrl.trim()
          })
        });
        if (response.ok) {
          toast.success('Web link asset created successfully!');
          navigate('/my-content');
        } else {
          const err = await response.json();
          toast.error(err.detail || 'Failed to create web link asset');
        }
      } catch (error) {
        toast.error('Failed to create web link asset: ' + error.message);
      } finally {
        setUploading(false);
      }
      return;
    }

    // --- File upload mode ---
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    if (formData.target_audiences.length === 0) {
      toast.error('Please select at least one target audience');
      return;
    }
    if (formData.conversation_enterprise_smc.length === 0 && formData.conversation_smb.length === 0) {
      toast.error('Please select at least one value under Conversation (Enterprise & SMC) or Conversation (SMB)');
      return;
    }
    if (!formData.content_build_date) {
      toast.error('Please set the Version (content build date)');
      return;
    }
    
    // Validate replacement selection
    if (formData.is_replacement && !formData.replacing_asset_id) {
      toast.error('Please select which asset to replace from the dropdown');
      return;
    }

    // Validate file type matches selected content type
    const validation = validateFileType(file, formData.content_type);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const blobName = await uploadToAzure(file, 'content');
      let captionBlobName = null;
      if (captionFile) {
        captionBlobName = await uploadToAzure(captionFile, 'caption');
      }

      const token = localStorage.getItem('token');
      
      // Prepare asset data - convert empty expiry_date to null for backend default handling
      const assetData = {
        ...formData,
        expiry_date: formData.expiry_date ? formData.expiry_date : null
      };
      
      // Use the upload/complete endpoint to create the asset record
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          asset_data: assetData,
          metadata: {
            file_name: blobName,
            file_size: file.size,
            content_type: file.type,
            caption_file_name: captionBlobName
          },
          caption_blob_name: captionBlobName
        })
      });

      if (response.ok) {
        toast.success('Asset uploaded successfully!');
        navigate('/my-content');
      } else {
        toast.error('Failed to create asset record');
      }
    } catch (error) {
      console.error(error);
      toast.error('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      <LevelUpHeader user={user} onLogout={onLogout} />
      <SunsetBanner user={user} />

      {/* COA Modal */}
      <COAModal
        isOpen={showCOAModal}
        onAccept={handleAcceptCOA}
        onClose={handleCancelCOA}
        loading={coaLoading}
      />

      <div style={{ flex: 1 }}>
        <div style={{ background: 'linear-gradient(135deg, #B9DCD2 0%, #E8F5E9 100%)', padding: '40px 24px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <button 
              onClick={() => navigate('/browse')} 
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#1B5E5F', fontSize: '14px', fontWeight: '500', marginBottom: '16px' }}
            >
              <ArrowLeft size={18} />
              Back to Why Microsoft – Compete Enablement for Partners
            </button>
            <h1 style={{ fontSize: '36px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Upload Content</h1>
            <p style={{ fontSize: '16px', color: '#5A6C7D' }}>Share compete assets with Microsoft partners</p>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 280px', gap: '32px' }}>
        <form onSubmit={handleSubmit} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2C3E50', marginBottom: '24px' }}>Enter Asset Details</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Solution Area *</label>
              <select name="solution_area" value={formData.solution_area} onChange={handleChange} required style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }}>
                <option value="">Select...</option>
                {Object.keys(solutionAreas).map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Content Type *</label>
              <select name="content_type" value={formData.content_type} onChange={handleChange} required style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }}>
                <option value="Presentation">Presentation</option>
                <option value="Video">Video</option>
                <option value="Excel Sheet">Excel Sheet</option>
                <option value="PDF">PDF</option>
                <option value="Word">Word</option>
                <option value="Web URL">Web URL</option>
              </select>
            </div>

            {Object.keys(conversationGroups).map(group => {
              const bothEmpty = formData.conversation_enterprise_smc.length === 0 && formData.conversation_smb.length === 0;
              const labelSuffix = bothEmpty ? ' *' : '';
              return (
              <div key={group} style={{ position: 'relative' }} data-testid={`conversations-field-${group}`}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>{group.replace('Enterprise & SMC', 'Enterprise\u00A0&\u00A0SMC')}{labelSuffix}</label>
                <button
                  type="button"
                  data-testid={`conversation-dropdown-trigger-${group}`}
                  onClick={() => setOpenConversationGroup(prev => prev === group ? null : group)}
                  style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: formData[CONVERSATION_GROUP_FIELD[group]].length ? '#2C3E50' : '#9CA3AF' }}
                >
                  <span>{formData[CONVERSATION_GROUP_FIELD[group]].length ? formData[CONVERSATION_GROUP_FIELD[group]].join(', ') : 'Select...'}</span>
                  <span style={{ fontSize: '10px', color: '#6B7280' }}>▾</span>
                </button>
                {openConversationGroup === group && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: '4px', backgroundColor: 'white', border: '1px solid #E0E0E0', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                    {(conversationGroups[group] || []).length === 0 && (
                      <span style={{ fontSize: '13px', color: '#95A5A6', padding: '4px' }}>No values configured yet — ask an Admin to add them under Taxonomy.</span>
                    )}
                    {(conversationGroups[group] || []).map(value => (
                      <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', borderRadius: '4px' }}>
                        <input
                          type="checkbox"
                          data-testid={`conversation-checkbox-${value}`}
                          checked={formData[CONVERSATION_GROUP_FIELD[group]].includes(value)}
                          onChange={() => handleConversationToggle(group, value)}
                          style={{ accentColor: '#0C6A1D' }}
                        />
                        <span style={{ fontSize: '14px' }}>{value}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              );
            })}

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Language *</label>
              <select name="language" value={formData.language} onChange={handleChange} required style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }}>
                {['English', 'Spanish', 'French', 'German', 'Portuguese', 'Japanese', 'Korean', 'Chinese (Simplified)', 'Chinese (Traditional)', 'Italian', 'Russian', 'Turkish', 'Dutch'].map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Version (Content Build Date) *</label>
              <input
                type="date"
                name="content_build_date"
                value={formData.content_build_date}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }}
              />
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>The date this content was created or last revised.</p>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Expiry Date</label>
              <input type="date" name="expiry_date" value={formData.expiry_date} onChange={handleChange} style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }} />
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>Optional. Defaults to 1 year from upload date if not specified.</p>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Asset Title *</label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} required style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }} />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Target Audience *</label>
            <div style={{ display: 'flex', gap: '16px' }}>
              {['Sales', 'Presales', 'Technical'].map(audience => (
                <label key={audience} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.target_audiences.includes(audience)} onChange={() => handleAudienceToggle(audience)} style={{ accentColor: '#0C6A1D' }} />
                  <span style={{ fontSize: '14px' }}>{audience}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Description *</label>
            <textarea name="description" value={formData.description} onChange={handleChange} required rows={4} style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px', resize: 'vertical' }} />
          </div>

          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#F5F5F5', borderRadius: '4px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
              <input type="checkbox" name="is_replacement" checked={formData.is_replacement} onChange={handleChange} style={{ accentColor: '#0C6A1D' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2C3E50' }}>This is a replacement/new version of existing asset</span>
            </label>
            {formData.is_replacement && (
              <select name="replacing_asset_id" value={formData.replacing_asset_id} onChange={handleChange} required style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px' }}>
                <option value="">Select asset to replace...</option>
                {myAssets.map(asset => (
                  <option key={asset.id} value={asset.id}>{asset.title} (v{asset.current_version})</option>
                ))}
              </select>
            )}
          </div>

          <div style={{ marginBottom: '24px' }}>
            {/* Mode Toggle */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '20px', border: '1px solid #E0E0E0', borderRadius: '6px', overflow: 'hidden', width: 'fit-content' }}>
              <button
                type="button"
                data-testid="mode-file"
                onClick={() => {
                  setUploadMode('file');
                  if (formData.content_type === 'Web URL') {
                    setFormData(prev => ({ ...prev, content_type: 'Presentation' }));
                  }
                }}
                style={{
                  padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                  display: 'flex', alignItems: 'center', gap: '7px',
                  backgroundColor: uploadMode === 'file' ? '#0C6A1D' : '#F5F5F5',
                  color: uploadMode === 'file' ? 'white' : '#5A6C7D',
                  transition: 'all 0.2s'
                }}
              >
                <UploadCloud size={16} /> Upload File
              </button>
              <button
                type="button"
                data-testid="mode-weblink"
                onClick={() => {
                  setUploadMode('weblink');
                  setFormData(prev => ({ ...prev, content_type: 'Web URL' }));
                }}
                style={{
                  padding: '10px 20px', border: 'none', borderLeft: '1px solid #E0E0E0', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                  display: 'flex', alignItems: 'center', gap: '7px',
                  backgroundColor: uploadMode === 'weblink' ? '#0C6A1D' : '#F5F5F5',
                  color: uploadMode === 'weblink' ? 'white' : '#5A6C7D',
                  transition: 'all 0.2s'
                }}
              >
                <Link size={16} /> Web URL
              </button>
            </div>

            {uploadMode === 'file' ? (
              <>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Upload File *</label>
                <input type="file" onChange={(e) => setFile(e.target.files[0])} required={uploadMode === 'file'} style={{ width: '100%', padding: '12px', border: '2px dashed #E0E0E0', borderRadius: '4px', backgroundColor: '#FAFAFA' }} />
                <p style={{ fontSize: '12px', color: '#95A5A6', marginTop: '8px' }}>Max 2GB. Supported: PPT, Video, Excel, Word, PDF</p>
              </>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Web URL *</label>
                <input
                  type="url"
                  data-testid="weblink-url-input"
                  value={webUrl}
                  onChange={e => setWebUrl(e.target.value)}
                  placeholder="https://example.com/resource"
                  required={uploadMode === 'weblink'}
                  style={{ width: '100%', padding: '12px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                />
                <p style={{ fontSize: '12px', color: '#95A5A6', marginTop: '8px' }}>Enter the full URL including https://. Users will see an "Explore" button that opens this link in a new tab.</p>
              </>
            )}
          </div>

          {formData.content_type === 'Video' && uploadMode === 'file' && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Caption File (Optional)</label>
              <input type="file" onChange={(e) => setCaptionFile(e.target.files[0])} style={{ width: '100%', padding: '12px', border: '2px dashed #E0E0E0', borderRadius: '4px', backgroundColor: '#FAFAFA' }} />
            </div>
          )}

          {uploading && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Uploading...</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#0C6A1D' }}>{progress}%</span>
              </div>
              <div style={{ width: '100%', height: '8px', backgroundColor: '#E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#0C6A1D', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => navigate('/my-content')} style={{ padding: '12px 24px', backgroundColor: '#E0E0E0', color: '#2C3E50', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={uploading} style={{ padding: '12px 24px', backgroundColor: '#0C6A1D', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
              {uploading ? 'Uploading...' : 'Submit'}
            </button>
          </div>
        </form>

        <aside>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Quick Links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => navigate('/browse')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                <Home size={18} color="#0C6A1D" />
                <span>Home</span>
              </button>
              <button onClick={() => navigate('/my-content')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                <FolderOpen size={18} color="#0C6A1D" />
                <span>My Content</span>
              </button>
              {user.roles.includes('admin') && (
                <button onClick={() => navigate('/admin')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                  <Settings size={18} color="#0C6A1D" />
                  <span>Admin Panel</span>
                </button>
              )}
              {(user.roles.includes('admin') || user.roles.includes('report_viewer')) && (
                <button onClick={() => navigate('/analytics')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                  <BarChart3 size={18} color="#0C6A1D" />
                  <span>Analytics</span>
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>
      </div>

      <LevelUpFooter />
    </div>
  );
}

export default Upload;
